/**
 * Chart Components - Eliza Forge Design System
 *
 * LineChart, BarChart, PieChart with DS theming.
 * Wraps Chart.js via react-chartjs-2.
 */

import * as React from "react"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend,
  type ChartData,
  type ChartOptions,
} from "chart.js"
import {
  Line as ChartJSLine,
  Bar as ChartJSBar,
  Pie as ChartJSPie,
} from "react-chartjs-2"
import { cn } from "../../shared/lib/cn"
import { Skeleton } from "./skeleton"

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
)

/* ============================================
   DS COLOR PALETTE
   ============================================ */

const DS_COLORS = [
  "rgb(var(--color-primary))",       // eliza-red
  "rgb(var(--color-text))",          // charcoal
  "#6B7280",                          // gray-500
  "#10B981",                          // green-500
  "#3B82F6",                          // blue-500
  "#F59E0B",                          // amber-500
  "#8B5CF6",                          // violet-500
  "#EC4899",                          // pink-500
]

function getColor(index: number): string {
  return DS_COLORS[index % DS_COLORS.length]
}

/* ============================================
   SHARED OPTIONS
   ============================================ */

function baseOptions(isDark: boolean): ChartOptions<"line" | "bar" | "pie"> {
  const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
  const tickColor = isDark ? "#9CA3AF" : "#6B7280"

  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          color: tickColor,
          font: { size: 12 },
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: isDark ? "rgb(var(--color-dark-surface-2))" : "rgb(var(--color-text))",
        titleColor: "#F9FAFB",
        bodyColor: "#F9FAFB",
        borderWidth: 0,
        cornerRadius: 8,
        padding: 10,
        bodyFont: { size: 12 },
        titleFont: { size: 12, weight: "bold" as const },
      },
    },
    scales: {
      x: {
        grid: { color: gridColor },
        ticks: { color: tickColor, font: { size: 11 } },
      },
      y: {
        grid: { color: gridColor },
        ticks: { color: tickColor, font: { size: 11 } },
      },
    },
  }
}

/* ============================================
   DETECT DARK MODE
   ============================================ */

function useIsDark(): boolean {
  const [dark, setDark] = React.useState(false)
  React.useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return
    }

    const match =
      typeof window.matchMedia === "function"
        ? window.matchMedia("(prefers-color-scheme: dark)")
        : null
    const el = document.documentElement
    const check = () => setDark(el.classList.contains("dark") || Boolean(match?.matches))
    check()
    const obs = new MutationObserver(check)
    obs.observe(el, { attributes: true, attributeFilter: ["class"] })

    if (match?.addEventListener) {
      match.addEventListener("change", check)
    } else if (match?.addListener) {
      match.addListener(check)
    }

    return () => {
      obs.disconnect()
      if (match?.removeEventListener) {
        match.removeEventListener("change", check)
      } else if (match?.removeListener) {
        match.removeListener(check)
      }
    }
  }, [])
  return dark
}

/* ============================================
   COMMON CHART PROPS
   ============================================ */

interface BaseChartProps {
  height?: number
  loading?: boolean
  emptyMessage?: string
  className?: string
}

function isEmpty(data: ChartData<"line" | "bar" | "pie">): boolean {
  return !data.datasets?.length || data.datasets.every(d => !d.data?.length)
}

/* ============================================
   LINE CHART
   ============================================ */

interface LineChartProps extends BaseChartProps {
  data: ChartData<"line">
  xAxisLabel?: string
  yAxisLabel?: string
}

const LineChart: React.FC<LineChartProps> = ({
  data,
  height = 280,
  loading,
  emptyMessage = "No data available",
  xAxisLabel,
  yAxisLabel,
  className,
}) => {
  const isDark = useIsDark()

  if (loading) {
    return <Skeleton variant="rounded" height={height} className={className} />
  }

  if (isEmpty(data as ChartData<"line" | "bar" | "pie">)) {
    return (
      <div className={cn("flex items-center justify-center text-small text-gray-500 dark:text-gray-400", className)} style={{ height }}>
        {emptyMessage}
      </div>
    )
  }

  const themed: ChartData<"line"> = {
    ...data,
    datasets: data.datasets.map((ds, i) => ({
      borderColor: getColor(i),
      backgroundColor: `${getColor(i)}20`,
      borderWidth: 2,
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0.3,
      fill: data.datasets.length === 1,
      ...ds,
    })),
  }

  const opts: ChartOptions<"line"> = {
    ...(baseOptions(isDark) as ChartOptions<"line">),
    scales: {
      ...(baseOptions(isDark).scales),
      x: {
        ...(baseOptions(isDark).scales as Record<string, unknown>)?.x as object,
        title: xAxisLabel ? { display: true, text: xAxisLabel, color: isDark ? "#9CA3AF" : "#6B7280", font: { size: 11 } } : undefined,
      },
      y: {
        ...(baseOptions(isDark).scales as Record<string, unknown>)?.y as object,
        title: yAxisLabel ? { display: true, text: yAxisLabel, color: isDark ? "#9CA3AF" : "#6B7280", font: { size: 11 } } : undefined,
      },
    },
  }

  return (
    <div className={className} style={{ height }}>
      <ChartJSLine data={themed} options={opts} />
    </div>
  )
}

/* ============================================
   BAR CHART
   ============================================ */

interface BarChartProps extends BaseChartProps {
  data: ChartData<"bar">
  horizontal?: boolean
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  height = 280,
  loading,
  emptyMessage = "No data available",
  horizontal = false,
  className,
}) => {
  const isDark = useIsDark()

  if (loading) {
    return <Skeleton variant="rounded" height={height} className={className} />
  }

  if (isEmpty(data as ChartData<"line" | "bar" | "pie">)) {
    return (
      <div className={cn("flex items-center justify-center text-small text-gray-500 dark:text-gray-400", className)} style={{ height }}>
        {emptyMessage}
      </div>
    )
  }

  const themed: ChartData<"bar"> = {
    ...data,
    datasets: data.datasets.map((ds, i) => ({
      backgroundColor: getColor(i),
      borderRadius: 4,
      ...ds,
    })),
  }

  const opts: ChartOptions<"bar"> = {
    ...(baseOptions(isDark) as ChartOptions<"bar">),
    indexAxis: horizontal ? ("y" as const) : ("x" as const),
  }

  return (
    <div className={className} style={{ height }}>
      <ChartJSBar data={themed} options={opts} />
    </div>
  )
}

/* ============================================
   PIE CHART
   ============================================ */

interface PieChartProps extends BaseChartProps {
  data: ChartData<"pie">
  donut?: boolean
}

const PieChart: React.FC<PieChartProps> = ({
  data,
  height = 280,
  loading,
  emptyMessage = "No data available",
  donut = false,
  className,
}) => {
  const isDark = useIsDark()

  if (loading) {
    return <Skeleton variant="rounded" height={height} className={className} />
  }

  if (isEmpty(data as ChartData<"line" | "bar" | "pie">)) {
    return (
      <div className={cn("flex items-center justify-center text-small text-gray-500 dark:text-gray-400", className)} style={{ height }}>
        {emptyMessage}
      </div>
    )
  }

  const themed: ChartData<"pie"> = {
    ...data,
    datasets: data.datasets.map(ds => ({
      backgroundColor: (ds.data as number[]).map((_, i) => getColor(i)),
      borderWidth: 2,
      borderColor: isDark ? "rgb(var(--color-dark-surface))" : "#FFFFFF",
      ...ds,
    })),
  }

  const opts: ChartOptions<"pie"> = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: donut ? "55%" : 0,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          color: isDark ? "#9CA3AF" : "#6B7280",
          font: { size: 12 },
          usePointStyle: true,
          pointStyle: "circle",
          padding: 16,
        },
      },
      tooltip: {
        backgroundColor: isDark ? "rgb(var(--color-dark-surface-2))" : "rgb(var(--color-text))",
        titleColor: "#F9FAFB",
        bodyColor: "#F9FAFB",
        borderWidth: 0,
        cornerRadius: 8,
        padding: 10,
      },
    },
  }

  return (
    <div className={className} style={{ height }}>
      <ChartJSPie data={themed} options={opts} />
    </div>
  )
}

/* ============================================
   CHART CONTAINER — wrapper with title & actions
   ============================================ */

interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string
  description?: string
  actions?: React.ReactNode
}

const ChartContainer = React.forwardRef<HTMLDivElement, ChartContainerProps>(
  ({ className, title, description, actions, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border border-gray-200 bg-white p-5",
        "dark:bg-dark-surface dark:border-dark-border",
        className
      )}
      {...props}
    >
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h3 className="font-subtitle text-base font-medium text-charcoal dark:text-gray-100">
            {title}
          </h3>
          {description && (
            <p className="mt-0.5 text-small text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2">{actions}</div>}
      </div>
      {children}
    </div>
  )
)
ChartContainer.displayName = "ChartContainer"

export { LineChart, BarChart, PieChart, ChartContainer }
export type { LineChartProps, BarChartProps, PieChartProps, ChartContainerProps, BaseChartProps }
